import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { formatBalance, formatDate } from '@ui/src';
import { BlacklistData } from '../../../types';
import { blacklistApi } from '../services';

export interface BlackListUserInfoState {
  info?: BlacklistData;
}

const initialState: BlackListUserInfoState = {};

export const blacklistUserInfoSlice = createSlice({
  name: 'blacklistUserInfo',
  initialState,
  reducers: {
    clearBlacklistUserInfoState: (state) => {
      state.info = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      blacklistApi.endpoints.getBlacklistDataItem.matchFulfilled,
      (state, action: PayloadAction<{ data: BlacklistData }>) => {
        state.info = {
          ...action.payload.data,
          source: {
            ...action.payload.data?.source,
            name:
              action.payload.data?.source?.firstName ||
              action.payload.data?.source?.lastName
                ? `${action.payload.data?.source?.firstName || ''} ${
                    action.payload.data?.source?.lastName || ''
                  } ${action?.payload?.data?.source?.middleName || ''}`
                : action.payload.data?.source?.name || '-',
            entityAddresses:
              action?.payload?.data?.source?.entityAddresses?.map(
                (item, index) => ({
                  ...item,
                  id: item.entityAddressGUID || `entityAddresses-${index}`,
                  lastUpdated: item?.lastUpdated
                    ? formatDate(item.lastUpdated)
                    : '—',
                  addressTypeDesc: item?.addressTypeDesc || '—',
                  address1: item?.address1 || '—',
                  address2: item?.address2 || '—',
                  city: item?.city || '—',
                  country: item?.country || '—',
                  isoStandard: item?.isoStandard || '—',
                  postalCode: item?.postalCode || '-',
                }),
              ),
            entityAliases: action?.payload?.data?.source?.entityAliases?.map(
              (item, index) => ({
                ...item,
                id: item?.entityAliasGUID || `entityAliases-${index}`,
                aliasTypeDesc: item?.aliasTypeDesc || '—',
                englishDescription: item?.englishDescription || '—',
                entityAliasGUID: item?.entityAliasGUID || '—',
                firstName: item?.firstName || '—',
                lastName: item?.lastName || '—',
                lastUpdated: item?.lastUpdated
                  ? formatDate(item.lastUpdated)
                  : '—',
                middleName: item?.middleName || '—',
                name: item?.name || '—',
              }),
            ),
            entityCountryAssociations:
              action?.payload?.data?.source?.entityCountryAssociations?.map(
                (item, index) => ({
                  ...item,
                  id:
                    item.entityCountryAssociationGUID ||
                    `entityCountryAssociations-${index}`,
                  administrativeUnitName: item?.administrativeUnitName || '—',
                  associationTypeDesc: item?.associationTypeDesc || '—',
                  entityCountryAssociationGUID:
                    item?.entityCountryAssociationGUID || '—',
                  isoStandard: item?.isoStandard || '—',
                  lastUpdated: item?.lastUpdated
                    ? formatDate(item.lastUpdated)
                    : '—',
                  ownershipPercentageCalc: item?.ownershipPercentageCalc
                    ? formatBalance(Number(item.ownershipPercentageCalc))
                    : '—',
                }),
              ),
            entityIdentifications:
              action?.payload?.data?.source?.entityIdentifications?.map(
                (item, index) => ({
                  ...item,
                  id:
                    item.entityIdentificationGUID ||
                    `entityIdentifications-${index}`,
                  administrativeUnitName: item?.administrativeUnitName || '—',
                  entityIdentificationGUID:
                    item?.entityIdentificationGUID || '—',
                  expirationDateValue: `${
                    item.expirationDate?.expirationYear || 'xxxx'
                  }-${
                    item?.expirationDate?.expirationMonth &&
                    Number(item?.expirationDate?.expirationMonth) < 10
                      ? '0'
                      : ''
                  }${item?.expirationDate?.expirationMonth || 'xx'}-${
                    item?.expirationDate?.expirationDay &&
                    Number(item?.expirationDate?.expirationDay) < 10
                      ? '0'
                      : ''
                  }${item?.expirationDate?.expirationDay || 'xx'}`,
                  identificationIssuer: item?.identificationIssuer || '—',
                  identificationNumber: item?.identificationNumber || '—',
                  identificationTypeDesc: item?.identificationTypeDesc || '—',
                  isoStandard: item?.isoStandard || '—',
                  issueDateValue: `${item.issueDate?.issueYear || 'xxxx'}-${
                    item?.issueDate?.issueMonth &&
                    Number(item?.issueDate?.issueMonth) < 10
                      ? '0'
                      : ''
                  }${item?.issueDate?.issueMonth || 'xx'}-${
                    item?.issueDate?.issueDay &&
                    Number(item?.issueDate?.issueDay) < 10
                      ? '0'
                      : ''
                  }${item?.issueDate?.issueDay || 'xx'}`,
                  lastUpdated: item?.lastUpdated
                    ? formatDate(item.lastUpdated)
                    : '—',
                }),
              ),
            entityPositions:
              action?.payload?.data?.source?.entityPositions?.map(
                (item, index) => ({
                  ...item,
                  id: item.entityPositionGUID || `entityPositions-${index}`,
                  entityPositionGUID: item.entityPositionGUID || '—',
                  lastUpdated: item?.lastUpdated
                    ? formatDate(item.lastUpdated)
                    : '—',
                  position: item.position || '—',
                }),
              ),
            entityRelationships:
              action?.payload?.data?.source?.entityRelationships?.map(
                (item, index) => ({
                  ...item,
                  id:
                    item.entityRelationshipGUID ||
                    `entityRelationships-${index}`,
                  entityRelationshipGUID: item?.entityRelationshipGUID || '—',
                  groupDesc: item?.groupDesc || '—',
                  lastUpdated: item?.lastUpdated
                    ? formatDate(item.lastUpdated)
                    : '—',
                  ownershipPercentage: item?.ownershipPercentage
                    ? formatBalance(Number(item.ownershipPercentage))
                    : '—',
                  relatedEntityGUID: item?.relatedEntityGUID,
                  relationshipDesc: item?.relationshipDesc || '—',
                }),
              ),
            entityRemarks: action?.payload?.data?.source?.entityRemarks?.map(
              (item, index) => ({
                ...item,
                entityRemarkGUID:
                  item.entityRemarkGUID || `entityRemarks-${index}`,
                lastUpdated: item?.lastUpdated
                  ? formatDate(item.lastUpdated)
                  : '—',
                remark: item?.remark || '—',
              }),
            ),
            entitySourceItems:
              action?.payload?.data?.source?.entitySourceItems?.map(
                (item, index) => ({
                  ...item,
                  entitySourceItemGUID:
                    item?.entitySourceItemGUID || `entitySourceItems-${index}`,
                  sourceURI: item?.sourceURI || '—',
                  lastUpdated: item?.lastUpdated
                    ? formatDate(item.lastUpdated)
                    : '—',
                }),
              ),
            entitySegments: {
              ...action?.payload?.data?.source?.entitySegments,
              entityAdverseMedias:
                action?.payload?.data?.source?.entitySegments?.entityAdverseMedias?.map(
                  (item, index) => ({
                    ...item,
                    entityAdverseMediaGUID:
                      item?.entityAdverseMediaGUID ||
                      `entityAdverseMedias-${index}`,
                    subCategories: item?.entityAdverseMediaSubcategories
                      ?.map((subCategory) => subCategory?.subCategoryLabel)
                      ?.join('; '),
                    lastUpdated: item?.lastUpdated
                      ? formatDate(item.lastUpdated)
                      : '—',
                  }),
                ),
              entityEnforcements:
                action?.payload?.data?.source?.entitySegments?.entityEnforcements?.map(
                  (item, index) => ({
                    ...item,
                    entityEnforcementGUID:
                      item?.entityEnforcementGUID ||
                      `entityEnforcements-${index}`,
                    subCategories: item?.entityEnforcementSubcategories
                      ?.map((subCategory) => subCategory?.subCategoryLabel)
                      ?.join('; '),
                    lastUpdated: item?.lastUpdated
                      ? formatDate(item.lastUpdated)
                      : '—',
                  }),
                ),
              entityPEPs:
                action?.payload?.data?.source?.entitySegments?.entityPEPs?.map(
                  (item, index) => ({
                    ...item,
                    id: item?.entityPEPGUID || `entityPEPs-${index}`,
                    effectiveDateValue: `${
                      item.effectiveDate?.effectiveYear || 'xxxx'
                    }-${
                      item?.effectiveDate?.effectiveMonth &&
                      Number(item?.effectiveDate?.effectiveMonth) < 10
                        ? '0'
                        : ''
                    }${item?.effectiveDate?.effectiveMonth || 'xx'}-${
                      item?.effectiveDate?.effectiveDay &&
                      Number(item?.effectiveDate?.effectiveDay) < 10
                        ? '0'
                        : ''
                    }${item?.effectiveDate?.effectiveDay || 'xx'} ${
                      item?.effectiveDateTypeDesc
                        ? `(${item.effectiveDateTypeDesc})`
                        : ''
                    }`,
                    effectiveDateTypeDesc: item?.expirationDateTypeDesc || '—',
                    entityPEPGUID: item?.entityPEPGUID || '—',
                    expirationDateValue: `${
                      item.expirationDate?.expirationYear || 'xxxx'
                    }-${
                      item?.expirationDate?.expirationMonth &&
                      Number(item?.expirationDate?.expirationMonth) < 10
                        ? '0'
                        : ''
                    }${item?.expirationDate?.expirationMonth || 'xx'}-${
                      item?.expirationDate?.expirationDay &&
                      Number(item?.expirationDate?.expirationDay) < 10
                        ? '0'
                        : ''
                    }${item?.expirationDate?.expirationDay || 'xx'} ${
                      item?.expirationDateTypeDesc
                        ? `(${item?.expirationDateTypeDesc})`
                        : ''
                    }`,
                    expirationDateTypeDesc: item?.expirationDateTypeDesc || '—',
                    governingInstitution: item?.governingInstitution || '—',
                    governingRole: item?.governingRole || '—',
                    isActivePEP: item?.isActivePEP || '—',
                    isInCountryPEPOnly: item?.isInCountryPEPOnly || '—',
                    isPrimaryPEP: item?.isPrimaryPEP || '—',
                    isoAdministrativeUnitLevel0:
                      item?.isoAdministrativeUnitLevel0 || '—',
                    lastUpdated: item?.lastUpdated
                      ? formatDate(item.lastUpdated)
                      : '—',
                    pepAdminLevelDesc: item?.pepAdminLevelDesc || '—',
                    flags:
                      item.isActivePEP === 'Y' || item?.isPrimaryPEP === '1'
                        ? `${item?.isPrimaryPEP === '1' ? 'Primary' : ''}${
                            item.isActivePEP === 'Y' ? `; Active` : ''
                          }`
                        : '—',
                  }),
                ),
              entitySanctions:
                action?.payload?.data?.source?.entitySegments?.entitySanctions?.map(
                  (item, index) => ({
                    ...item,
                    id: item.entitySanctionGUID || `entitySanctions-${index}`,
                    administrativeUnitName: item?.administrativeUnitName || '—',
                    consolidated: item?.consolidatedSanction ? 'Yes' : 'No',
                    designations:
                      item?.entitySanctionDesignations &&
                      item?.entitySanctionDesignations?.length
                        ? item?.entitySanctionDesignations
                            ?.map(
                              (designation) =>
                                `${designation?.designationName} / ${designation?.subDesignationName}`,
                            )
                            ?.join('; ')
                        : '—',
                    entitySanctionGUID: item?.entitySanctionGUID || '—',
                    isoStandard: item?.isoStandard || '—',
                    lastUpdated: item?.lastUpdated
                      ? formatDate(item.lastUpdated)
                      : '—',
                    sourceName: item?.sourceName || '—',
                    sourceNameAbbrev: item?.sourceNameAbbrev || '—',
                  }),
                ),
              entitySOEs:
                action?.payload?.data?.source?.entitySegments?.entitySOEs?.map(
                  (item, index) => ({
                    ...item,
                    id: item.entitySOEGUID || `entitySOEs-${index}`,
                    domains:
                      item?.entitySOEDomains &&
                      item?.entitySOEDomains?.length &&
                      item?.entitySOEDomains?.filter(
                        (domain) => domain.soeDomainDesc,
                      )?.length
                        ? item?.entitySOEDomains
                            ?.map((domain) => domain?.soeDomainDesc)
                            ?.join('; ')
                        : '—',
                    entitySOEGUID: item?.entitySOEGUID || '—',
                    subCategories:
                      item?.entitySOESubcategories &&
                      item?.entitySOESubcategories?.length &&
                      item?.entitySOESubcategories?.filter(
                        (category) => category?.subCategoryLabel,
                      )?.length
                        ? item?.entitySOESubcategories
                            ?.map((category) => category?.subCategoryLabel)
                            ?.join('; ')
                        : '—',
                    isActive: item?.isActive === '1' ? 'Active' : 'Inactive',
                    lastUpdated: item?.lastUpdated
                      ? formatDate(item.lastUpdated)
                      : '—',
                  }),
                ),
              additionalSegments:
                action?.payload?.data?.source?.entitySegments?.additionalSegments?.map(
                  (item) => ({
                    ...item,
                    additionalSegmentFields: item?.additionalSegmentFields?.map(
                      (segment, index) => ({
                        ...segment,
                        id: `${item?.type
                          ?.split(' ')
                          ?.join('-')
                          ?.toLowerCase()}-${index}`,
                      }),
                    ),
                  }),
                ),
            },
          },
        };
      },
    );
  },
});

export const { clearBlacklistUserInfoState } = blacklistUserInfoSlice.actions;

export default blacklistUserInfoSlice.reducer;
